import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { api } from '../lib/api';
import { formatCurrency } from '@/lib/format';
import { toast } from 'sonner';

const errText = (e, fallback) => {
    const d = e?.response?.data?.detail;
    return typeof d === 'string' ? d : fallback;
};

function todayIstanbulWallClock() {
    const p = {};
    new Intl.DateTimeFormat('tr-TR', {
        timeZone: 'Europe/Istanbul', year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', hour12: false,
    }).formatToParts(new Date()).forEach(x => { p[x.type] = x.value; });
    return `${p.year}-${p.month}-${p.day}T${p.hour}:${p.minute}`;
}

export function JobDiscountForm({ onCancel, jobId, remaining, onSuccess }) {
    const [amount, setAmount] = useState('');
    const [date, setDate] = useState(todayIstanbulWallClock());
    const [reason, setReason] = useState('');
    const [saving, setSaving] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (saving) return;
        setSaving(true);
        try {
            await api.post(`/jobs/${jobId}/discounts`, {
                amount: Number(amount),
                discount_date: `${date}:00+03:00`,
                reason,
            });
            toast.success('İskonto eklendi');
            onSuccess();
            onCancel();
        } catch (err) {
            toast.error(errText(err, 'İskonto kaydedilemedi'));
        } finally {
            setSaving(false);
        }
    };

    return (
        <div>
            <p className="text-sm text-gray-500">
                İskonto tahsilat sayılmaz; yalnızca işin kalan alacağını azaltır.
                {remaining != null && ` Kalan alacak: ${formatCurrency(remaining)}`}
            </p>
            <form onSubmit={submit} className="grid gap-4 py-2">
                <div className="space-y-2">
                    <Label>Tarih</Label>
                    <Input type="datetime-local" required value={date} onChange={e => setDate(e.target.value)} />
                </div>
                <div className="space-y-2">
                    <Label>Tutar (TL)</Label>
                    <Input type="number" step="0.01" min="0.01" required value={amount}
                        onChange={e => setAmount(e.target.value)} data-testid="discount-amount" />
                </div>
                <div className="space-y-2">
                    <Label>Gerekçe</Label>
                    <Textarea required maxLength={500} value={reason}
                        onChange={e => setReason(e.target.value)} data-testid="discount-reason" />
                </div>
                <DialogFooter>
                    <Button type="button" variant="outline" onClick={() => onCancel()}>İptal</Button>
                    <Button type="submit" disabled={saving || !amount || !reason.trim()} className="bg-brand-red hover:bg-brand-red-hover text-white">
                        {saving ? 'Kaydediliyor...' : 'İskontoyu Kaydet'}
                    </Button>
                </DialogFooter>
            </form>
        </div>
    );
}

export function CancelJobDiscountModal({ open, onOpenChange, jobId, discount, onSuccess }) {
    const [reason, setReason] = useState('');
    const [saving, setSaving] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (saving || !reason.trim()) return;
        setSaving(true);
        try {
            await api.post(`/jobs/${jobId}/discounts/${discount.id}/cancel`, { cancellation_reason: reason });
            toast.success('İskonto iptal edildi');
            onSuccess();
            onOpenChange(false);
        } catch (err) {
            toast.error(errText(err, 'İskonto iptal edilemedi'));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[440px]">
                <DialogHeader>
                    <DialogTitle>İskontoyu İptal Et</DialogTitle>
                    <DialogDescription>
                        {formatCurrency(discount?.amount)} tutarındaki iskonto iptal edilecek ve alacak geri yüklenecek. Kayıt geçmişte görünür kalır.
                    </DialogDescription>
                </DialogHeader>
                <form onSubmit={submit} className="grid gap-4 py-2">
                    <div className="space-y-2">
                        <Label>İptal Gerekçesi *</Label>
                        <Textarea required maxLength={500} value={reason}
                            onChange={e => setReason(e.target.value)} data-testid="discount-cancel-reason" />
                    </div>
                    <DialogFooter>
                        <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Vazgeç</Button>
                        <Button type="submit" variant="destructive" disabled={saving || !reason.trim()}>
                            {saving ? 'İptal ediliyor...' : 'İskontoyu İptal Et'}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}
